import React from "react"
import { Navigate, useLocation } from "react-router-dom"
import { Box, IconButton, Tooltip } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { MaximalLayout } from "@app"

import { useUserApp } from "@hooks"
import { isLocal, isUserMaster, userData } from "@signals"
import { HOME_ROUTES, SESSIONS_ROUTES, USER_ROUTES } from "@constants"
import { BUSINESS_LOGISTICS_ROUTES } from "@constants/routes"
import { dsColorNew } from "@palette"
import { appConfig, styles } from "./config"
import routes from "../Routes"
import SlavesAutocomplete from "Layout/Slaves"
import PmsAgentWidget, { isIrisUser } from "Layout/PmsAgentWidget"
import IrisLauncher from "Layout/IrisLauncher"
import SupportChatButton from "Layout/SupportChat"
import { AppNewsButton, AppNewsPopup } from "@components/News"
import IrisMark from "@components/brand/IrisMark"
import { userMenuLinks, withMenuEntry, withSettingsDivider } from "./menu"


function HeaderTools() {
	return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
		{isUserMaster.value && <SlavesAutocomplete/>}
		<Tooltip title="User Guide" arrow>
			<IconButton
				size="small"
				onClick={() => {
					const guide = navigator.language?.startsWith('es') ? '/assets/user-guide-es.html' : '/assets/user-guide.html'
					window.open(guide, '_blank')
				}}
				sx={{
					color: dsColorNew.textMuted,
					'&:hover': { color: dsColorNew.webBlue500, bgcolor: dsColorNew.hoverBgLight },
				}}
			>
				<IconSeeker icon="book-open" sx={{ fontSize: 16 }}/>
			</IconButton>
		</Tooltip>
		<SupportChatButton/>
		<AppNewsButton/>
	</Box>
}


export default function AppLayout() {
	const { pathname, search } = useLocation()
	const { isLogged } = useUserApp(true)
	// The separator above User Settings goes in after the feature filter, on the menu only (CU-869fmherf D5)
	const features = userData?.value?.featuresV2
	const menuRoutes = withSettingsDivider(
		routes(userData.value).filter(r => !r.noRender && (features && features[r.id])),
		USER_ROUTES.PATH,
	)
	// Europcar Custom Search Analytics is listed under Main Page, after Search Analytics; its route stays in
	// Business Intelligence (`noMenu` there), so the URL does not change (CU-869fmherf batch 5)
	const renderRoutes = features?.europcar
		? withMenuEntry(menuRoutes, HOME_ROUTES.PATH, HOME_ROUTES.SEARCH_ANALYTICS_PATH, {
			path: BUSINESS_LOGISTICS_ROUTES.EUROPCAR_PATH, title: BUSINESS_LOGISTICS_ROUTES.EUROPCAR_MENU_TITLE,
		})
		: menuRoutes
	// Settings and, for Iris's users, Iris settings in the user menu (D2); Iris's entry wears her mark
	const userLinks = userMenuLinks({
		isIris: isIrisUser(userData.value), settingsPath: USER_ROUTES.SETTINGS_PATH, irisPath: USER_ROUTES.IRIS_PATH,
	}).map(({ iris, ...link }) => iris ? { ...link, icon: <IrisMark size={16}/> } : link)

	if (!isLogged) return <Navigate
		to={`${SESSIONS_ROUTES.LOGIN_URL}?redirect=${pathname}${search}`}
		replace
	/>

	return <>
		<PmsAgentWidget/>
		<AppNewsPopup/>
		<MaximalLayout
			userData={userData.value}
			routes={renderRoutes}
			links={userLinks}
			config={appConfig}
			isLocal={isLocal.value}
			components={{ headerTools: <HeaderTools/>, navFooter: <IrisLauncher/> }}
			styles={styles}
		/>
	</>
}